import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { S3Avatar, S3Badge, S3EmptyState, S3Modal } from './v3/primitives';
import { initialsOf } from './constants';
import { photoUrl } from '../../utils/photoUrl';
import { INVITE_DEBOUNCE_MS, INVITE_MIN_CHARS, inviteDisplayName, inviteRowMeta, mergeInvitePages } from './inviteModel';
import type { InvitableResult, InviteCandidate } from '../../services/supportApi';
// Condivisa con l'area rappresentante: i fogli scoped vanno importati qui (come AssigneesModal).
import '../../styles/admin-v2.css';
import '../../styles/support-v3.css';

export interface InviteParticipantModalProps {
  /** Ricerca paginata (`api.searchInvitable` legato al ticket). */
  search: (q: string, page: number) => Promise<InvitableResult>;
  /** Scelta di una riga: il chiamante chiude e decide (menzione o invito manuale). */
  onPick: (c: InviteCandidate) => void;
  onClose: () => void;
  busy?: boolean;
}

/**
 * Popup «Invita» (spec 2026-10-15, parte B): cerca rappresentanti e
 * amministratori fuori dal canale; chi è già nel ticket resta visibile ma
 * disabilitato. Stesso markup nelle due vesti (S3Modal porta `.adm2 .sup3`).
 */
const InviteParticipantModal: React.FC<InviteParticipantModalProps> = ({ search, onPick, onClose, busy = false }) => {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<InviteCandidate[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  // Il backend ha letto il massimo di righe: risultati forse incompleti.
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const seq = useRef(0);
  // In una ref: il chiamante può passare una funzione nuova a ogni render.
  const searchRef = useRef(search);
  searchRef.current = search;

  const run = (q: string, p: number) => {
    const mine = ++seq.current;
    setLoading(true);
    setError(false);
    searchRef.current(q, p)
      .then((res) => {
        if (seq.current !== mine) return;
        setItems((prev) => (p === 1 ? res.items : mergeInvitePages(prev, res.items)));
        setPage(res.page);
        setHasMore(res.hasMore);
        setTruncated(Boolean(res.truncated));
      })
      .catch(() => {
        if (seq.current !== mine) return;
        setError(true);
        // Pagina successiva fallita: i risultati gia mostrati restano e
        // «Mostra altri» pure, per riprovare.
        if (p === 1) {
          setItems([]);
          setHasMore(false);
          setTruncated(false);
        }
      })
      .finally(() => { if (seq.current === mine) setLoading(false); });
  };

  useEffect(() => {
    const q = query.trim();
    if (q.length < INVITE_MIN_CHARS) {
      seq.current += 1;
      setItems([]);
      setHasMore(false);
      setTruncated(false);
      setLoading(false);
      setError(false);
      return undefined;
    }
    // In attesa della battuta successiva: «Ricerca…» e nessun «Nessuno trovato.»
    // riferito alla ricerca precedente; una risposta ancora in volo va ignorata.
    seq.current += 1;
    setLoading(true);
    setError(false);
    const timer = setTimeout(() => run(q, 1), INVITE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const q = query.trim();
  return (
    <S3Modal open onClose={onClose} labelledBy="s3-invite-title">
      <div className="s3-mod">
        <div className="s3-mod__head">
          <h3 className="s3-h3" id="s3-invite-title">Invita nel ticket</h3>
          <button type="button" className="s3-pill s3-pill--sm s3-pill--icon" onClick={onClose} aria-label="Chiudi">
            <X size={14} />
          </button>
        </div>

        <div className="s3-mod__body">
          <input
            type="search"
            className="s3-search s3-search--plain"
            // S3Modal porta il focus qui (non sulla X): si scrive subito, anche
            // arrivando da tastiera con Invio su «Invita…» nell'elenco @.
            data-autofocus
            placeholder="Nome, studio, P. IVA, email, carica o provincia (min 2 caratteri)"
            aria-label="Cerca chi invitare"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="s3-pick__sub">
            Chi inviti vede solo questo ticket: legge, scrive note interne e propone risposte che il canale approva.
          </span>
          {/* Stati della ricerca in un'unica regione annunciata ai lettori di schermo. */}
          <div role="status" aria-live="polite">
            {truncated && !loading && <span className="s3-pick__sub">Troppi risultati: affina la ricerca</span>}
            {loading && <span className="s3-pick__sub">Ricerca…</span>}
            {error && <span className="s3-pick__sub">Ricerca non riuscita, riprova.</span>}
            {!loading && !error && q.length >= INVITE_MIN_CHARS && items.length === 0 && (
              <S3EmptyState>Nessuno trovato.</S3EmptyState>
            )}
          </div>
          {items.length > 0 && (
            <ul className="s3-pick" aria-label="Risultati della ricerca" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {items.map((c) => {
                const nome = inviteDisplayName(c);
                const meta = inviteRowMeta(c);
                return (
                  <li key={`${c.actorType}:${c.actorId}`} className="s3-pick__opt" style={{ justifyContent: 'space-between' }}>
                    <span className="s3-pick__txt s3-pick__txt--row">
                      <S3Avatar initials={initialsOf(nome)} size={20} src={photoUrl(c.foto)} title={nome} />
                      <span>
                        {nome}
                        {c.actorType === 'user' && <S3Badge tone="chip" small>admin</S3Badge>}
                        {meta && <span className="s3-pick__sub" style={{ display: 'block' }}>{meta}</span>}
                      </span>
                    </span>
                    {c.inTicket ? (
                      <button type="button" className="s3-pill s3-pill--sm" disabled>Già nel ticket</button>
                    ) : (
                      <button
                        type="button"
                        className="s3-pill s3-pill--sm s3-pill--primary"
                        disabled={busy}
                        aria-label={`Invita ${nome}`}
                        onClick={() => onPick(c)}
                      >
                        Invita
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {hasMore && !loading && (
            <button type="button" className="s3-pill s3-pill--sm" onClick={() => run(q, page + 1)}>
              Mostra altri
            </button>
          )}
        </div>

        <div className="s3-mod__foot">
          <button type="button" className="s3-pill" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </S3Modal>
  );
};

export default InviteParticipantModal;
